/**
 * Month-level day tally strip — proves that every day of the month is accounted for
 * and shows how the net payable days were arrived at.
 */
import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle2, AlertTriangle, CalendarRange } from 'lucide-react';

const Tile = ({ label, value, tone = 'slate', testid }) => {
  const tones = {
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    dark: 'bg-[#DC2626] border-[#DC2626] text-white',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${tones[tone]}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-lg font-bold leading-tight">{value}</p>
    </div>
  );
};

export function MonthTallySummary({ data, month }) {
  const t = data?.totals;
  if (!t) return null;
  const emp = Math.max(1, t.employees);
  const expected = t.month_days * emp;
  const accounted = t.present_days + t.approved_leave_days + t.lwp_days
    + (t.holiday_days * emp) + (t.week_off_days * emp) + t.absent_days;
  const ok = Math.abs(accounted - expected) < 0.05 && !t.not_tallied && !t.absent_days;

  return (
    <Card data-testid="month-tally-summary">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-semibold flex items-center gap-2 text-slate-800">
            <CalendarRange className="w-4 h-4 text-[#DC2626]" />
            Month Day Tally — {month}
          </p>
          <div className="flex items-center gap-2">
            {ok ? (
              <Badge className="bg-emerald-100 text-emerald-800" data-testid="month-tally-ok">
                <CheckCircle2 className="w-3 h-3 mr-1" /> All {t.month_days} days tally for every staff member — nothing to fix
              </Badge>
            ) : (
              <Badge className="bg-red-100 text-red-800" data-testid="month-tally-bad">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {t.not_tallied || 0} staff {(t.not_tallied || 0) === 1 ? 'member has' : 'members have'} days that do not tally
              </Badge>
            )}
            {t.absent_days > 0 && (
              <Badge variant="outline" className="border-amber-300 text-amber-700" data-testid="month-tally-unmatched">
                {t.absent_days} unaccounted day(s) in total
              </Badge>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          <Tile label="Days in month" value={t.month_days} tone="dark" testid="tile-month-days" />
          <Tile label="Weekly offs" value={t.week_off_days} tone="blue" testid="tile-week-offs" />
          <Tile label="Holidays (HR)" value={t.holiday_days} tone="amber" testid="tile-holidays" />
          <Tile label="Working days" value={t.working_days} tone="slate" testid="tile-working-days" />
          <Tile label="Present (all staff)" value={t.present_days} tone="green" testid="tile-present" />
          <Tile label="Paid leaves" value={t.approved_leave_days} tone="blue" testid="tile-leaves" />
          <Tile label="LWP" value={t.lwp_days} tone="red" testid="tile-lwp" />
          <Tile label="Absent (unpaid)" value={t.absent_days} tone="red" testid="tile-absent" />
        </div>

        <p className="text-xs text-slate-600 leading-relaxed" data-testid="month-tally-formula">
          <strong>Per staff member:</strong> {t.month_days} days = Present + Paid Leaves + LWP + {t.week_off_days} Weekly
          Offs + {t.holiday_days} Holidays + Absent.{' '}
          <strong>Net Payable</strong> = {t.month_days} − LWP − Absent − Late-mark deduction.
          Total net payable across {t.employees} staff: <strong>{t.payable_days}</strong> day(s).
        </p>
      </CardContent>
    </Card>
  );
}

export default MonthTallySummary;
